import React, { useEffect, useState, useRef } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import handWrong from "../../assets/hand-wrong.png";
import logoMedical from "../../assets/logo-medical.png";
import { useLang } from "../../translations/i18n";
import { ROUTES } from "../../utils/constants";
import { getScenarioByScreen, handleNavigate } from "../../utils/helpers";
import { modalInformeMedicoWithContent, modalQuestionInfo } from "../../utils/logger";
import { getTexts } from "../../api/text";
import { SERVER_URL } from "../../api";

function buildAvatarUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function parseConversationMessages(value) {
	if (!value || typeof value !== "string") return null;
	try {
		const arr = JSON.parse(value);
		return Array.isArray(arr) && arr.length > 0
			? arr.filter(
					(m) =>
						m &&
						(m.speaker === "doctor" ||
							m.speaker === "doctora" ||
							m.speaker === "patient" ||
							m.speaker === "prompt")
			  )
			: null;
	} catch {
		return null;
	}
}

function escapeHtml(text) {
	if (text == null || typeof text !== "string") return "";
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

const DEFAULT_ENUNCIADO_TITLE = "Ajuste de dosis";
const DEFAULT_ENUNCIADO = "Ha transcurrido un mes desde la última visita de Martina. <br> Estás en tu consulta cuando recibes una llamada.";
const DEFAULT_TEST_TITLE = "¿Sabes cómo ajustar voclosporina en función de la disminución de la tasa de filtración glomerular estimada (TFGe)?<br> Relaciona los valores de disminución confirmada de la TFGe desde el inicio con las recomendaciones de dosificación de voclosporina. Pulsa <span class=\"bold\">Confirmar</span> para comprobar si has acertado.";
const DEFAULT_INFORME_TITLE = "Informe";
const DEFAULT_PACIENTE = "Martina Ferrater";
const DEFAULT_EDAD = "37 años";

function Scenario6Intro({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const prevState = useLocation();
	const group = useSelector((state) => state.group);
	const lastMessageRef = useRef(null);

	const [messages, setMessages] = useState(null);
	const [avatarDoctorUrl, setAvatarDoctorUrl] = useState(null);
	const [avatarDoctoraUrl, setAvatarDoctoraUrl] = useState(null);
	const [avatarPatientUrl, setAvatarPatientUrl] = useState(null);
	const [avatarsReady, setAvatarsReady] = useState(false);
	const [enunciadoTitle, setEnunciadoTitle] = useState(DEFAULT_ENUNCIADO_TITLE);
	const [enunciado, setEnunciado] = useState(DEFAULT_ENUNCIADO);
	const [testTitle, setTestTitle] = useState(DEFAULT_TEST_TITLE);
	const [informeTitle, setInformeTitle] = useState(DEFAULT_INFORME_TITLE);
	const [informeContent, setInformeContent] = useState("");
	const [paciente, setPaciente] = useState(DEFAULT_PACIENTE);
	const [edad, setEdad] = useState(DEFAULT_EDAD);

	useEffect(() => {
		const gameId = group?.game;
		if (!gameId) return;

		setAvatarsReady(false);

		getTexts({ game: gameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const map = res.data.reduce(
						(acc, t) => ({ ...acc, [t.key]: t.value }),
						{}
					);

					const parsed = parseConversationMessages(
						map["scenario6-intro.conversation.messages"]
					);
					setMessages(parsed);

					setAvatarDoctorUrl(buildAvatarUrl(map["avatar.doctor"]));
					setAvatarDoctoraUrl(buildAvatarUrl(map["avatar.doctora"]));
					setAvatarPatientUrl(buildAvatarUrl(map["avatar.patient"]));

					if (map["scenario6-intro.enunciado-title"] != null && map["scenario6-intro.enunciado-title"] !== "") setEnunciadoTitle(map["scenario6-intro.enunciado-title"]);
					if (map["scenario6-intro.enunciado"] != null && map["scenario6-intro.enunciado"] !== "") setEnunciado(map["scenario6-intro.enunciado"]);
					if (map["scenario6-intro.test-title"] != null && map["scenario6-intro.test-title"] !== "") setTestTitle(map["scenario6-intro.test-title"]);
					if (map["scenario6-intro.informe-title"] != null && map["scenario6-intro.informe-title"] !== "") setInformeTitle(map["scenario6-intro.informe-title"]);
					if (map["scenario6-intro.informe"] != null && map["scenario6-intro.informe"] !== "") setInformeContent(map["scenario6-intro.informe"]);
					if (map["scenario1.intro.paciente"] != null && map["scenario1.intro.paciente"] !== "") setPaciente(map["scenario1.intro.paciente"]);
					if (map["scenario1.intro.edad"] != null && map["scenario1.intro.edad"] !== "") setEdad(map["scenario1.intro.edad"]);
				}
				setAvatarsReady(true);
			})
			.catch(() => {
				setAvatarsReady(true);
			});
	}, [group?.game]);

	const getAvatarUrl = (speaker) => {
		if (speaker === "doctor") return avatarDoctorUrl;
		if (speaker === "doctora") return avatarDoctoraUrl;
		return avatarPatientUrl;
	};

	const getWrapperClassForMessage = (speaker) => {
		if (speaker === "doctora") return "patient"; // 🔥 SOLO patient
		return speaker;
	};
	

	const renderMessageContent = (content) => {
		if (content && typeof content === "string" && content.indexOf("<") >= 0) {
			return <span dangerouslySetInnerHTML={{ __html: content }} />;
		}
		return content;
	};

	const renderChatContent = () => {
		if (!avatarsReady) {
			return (
				<div className="d-flex align-items-center justify-content-center py-5">
					<div className="txtWhite">Cargando conversación...</div>
				</div>
			);
		}
		if (messages && messages.length > 0) {
			return (
				<div className="d-flex flex-column conversation-chat">
					{messages.map((msg, index) => {
						const wrapperClass = getWrapperClassForMessage(msg.speaker);
						const isLeft = msg.speaker === "doctor";
						const isPrompt = msg.speaker === "prompt";
						const isRight = msg.speaker === "patient" || msg.speaker === "doctora";

						return (
							<div
								key={index}
								className={`conversation-message ${wrapperClass}`}
								ref={index === messages.length - 1 ? lastMessageRef : null}
							>
								{isPrompt ? (
									<div className="conversation-prompt">
										{renderMessageContent(msg.content)}
									</div>
								) : isLeft ? (
									<>
										<div className="conversation-avatar">
											{getAvatarUrl("doctor") ? (
												<img
													src={getAvatarUrl("doctor")}
													alt="Doctor"
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
											) : (
												<div
													style={{
														width: "100%",
														height: "100%",
														background: "#e0e8ec",
														borderRadius: "50%",
													}}
													aria-hidden
												/>
											)}
										</div>

										<div className="conversation-bubble">
											{renderMessageContent(msg.content)}
										</div>
									</>
								) : isRight ? (
									<>
										<div
											className="conversation-bubble"
											style={
												msg.speaker === "doctora"
													? { backgroundColor: "#7BE495" }
													: undefined
											}
										>
											{renderMessageContent(msg.content)}
										</div>

										<div className="conversation-avatar">
											{getAvatarUrl(msg.speaker) ? (
												<img
													src={getAvatarUrl(msg.speaker)}
													alt={msg.speaker === "doctora" ? "Doctora" : "Paciente"}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
											) : (
												<div
													style={{
														width: "100%",
														height: "100%",
														background: "#e8e8a0",
														borderRadius: "50%",
													}}
													aria-hidden
												/>
											)}
										</div>
									</>
								) : null}
							</div>
						);
					})}
				</div>
			);
		}
		// Sin mensajes desde API: placeholders en lugar de imágenes por defecto
		return (
			<div className="d-flex flex-column conversation-chat">
				{[0, 1, 2, 3, 4, 5].map((index) => (
					<div
						key={index}
						className={
							index % 2 === 0
								? "d-flex justify-content-start"
								: "d-flex justify-content-end"
						}
						ref={index === 5 ? lastMessageRef : null}
					>
						<div
							style={{
								maxWidth: index % 2 === 0 ? 607 : 538,
								height: 80,
								background: "#e0e0e0",
								borderRadius: 12,
								width: "80%",
							}}
							aria-hidden
						/>
					</div>
				))}
			</div>
		);
	};

	return (
		<div className="h-100">
			<div className="container-fluid bg-internas">
				<div style={{ paddingTop: "6vh", paddingLeft: "6vh" }}>
					<img width={240} src={logoMedical} alt="" />
				</div>

				<div
					style={{ paddingLeft: "6vh" }}
					className="row d-flex justify-content-start align-items-start mt-5"
				>
					<div className="col-4 txtWhite">
						<div className="txtWhite txt20" style={{ letterSpacing: 3 }}>
							ESCENARIO 6
						</div>
						<div
							className="txtWhite txt30 bold mt-3"
							style={{ letterSpacing: 3, lineHeight: "35px" }}
						>
							{enunciadoTitle && enunciadoTitle.indexOf("<") >= 0 ? (
								<span dangerouslySetInnerHTML={{ __html: enunciadoTitle }} />
							) : (
								enunciadoTitle
							)}
						</div>
						<div style={{ lineHeight: "20px" }} className="mt-4">
							{enunciado && enunciado.indexOf("<") >= 0 ? (
								<span dangerouslySetInnerHTML={{ __html: enunciado }} />
							) : (
								enunciado
							)}
						</div>
					</div>

					<div
						className="col-8"
						style={{ marginTop: messages && messages.length > 0 ? 0 : "-15vh" }}
					>
						{messages && messages.length > 0 ? (
							<div
								style={{
									height: "60vh",
									overflowY: "auto",
									overflowX: "visible",
									paddingLeft: 8,
									paddingRight: 8,
								}}
							>
								{renderChatContent()}
							</div>
						) : (
							renderChatContent()
						)}
					</div>

					<div
						className="container"
						style={{
							position: "absolute",
							right: "7vh",
							bottom: "4vh",
						}}
					>
						<div className="d-flex justify-content-end">
							<div
								className="btn-main-cancel me-3"
								onClick={() => {
									const title = informeTitle || DEFAULT_INFORME_TITLE;
									const pac = paciente || DEFAULT_PACIENTE;
									const age = edad || DEFAULT_EDAD;
									const body = informeContent || "";
									// Evitar que <p> en paciente/edad provoquen salto de línea (convertir a inline)
									const pacInline = (pac || "").replace(/<p>/gi, "<span>").replace(/<\/p>/gi, "</span>");
									const ageInline = (age || "").replace(/<p>/gi, "<span>").replace(/<\/p>/gi, "</span>");
									const html = `<div>
										<div class="d-flex align-items-center justify-content-between">
											<img src=${logoMedical} />
											<div class="txt-dark-green text-left" style="line-height: 1.4;">
												<div class="bold">${title}</div>
												<div class="d-flex align-items-baseline flex-nowrap"><span class="me-1">Paciente:</span><span>${pacInline}</span></div>
												<div class="d-flex align-items-baseline flex-nowrap"><span class="me-1">Edad:</span><span>${ageInline}</span></div>
											</div>
										</div>
										<div class="text-left mt-4 txt-black bullet-black w-75">${body}</div>
									</div>`;
									modalInformeMedicoWithContent(html);
								}}
							>
								<div className="txt-btn-main">VER INFORME</div>
							</div>

							<div
								className="btn-main me-3"
								onClick={() => {
									const safe = (testTitle || DEFAULT_TEST_TITLE).replace(/\\/g, "\\\\").replace(/`/g, "\\`").replace(/\$/g, "\\$");
									const modalHtml = `<div class="txt-black text-center"><div class="txt-turquoise bold">${safe}</div><div class="mt-4">Cada intento con al menos un error:</div><div class="d-flex justify-content-center mt-4"><div class="bg-incorrect txt-black w-fit-content p-2"><img width="20" src=${handWrong} class="me-1" />-10 puntos</div></div><div class="mt-4">(hasta un máximo de -50 puntos).</div></div>`;
									modalQuestionInfo({ html: modalHtml }).then((res) => {
										if (res.isConfirmed)
											handleNavigate({
												group: group,
												navigate: navigate,
												dispatch: dispatch,
												navigateTo: ROUTES.SCENARIO6,
												scenarioNum: getScenarioByScreen(ROUTES.SCENARIO6),
											});
									});
								}}
							>
								<div className="txt-btn-main">AVANZAR</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default injectIntl(Scenario6Intro);
